
/* Browser-local PDF export. Static PDF resources are loaded only when needed. */
(function (global) {
  'use strict';

  let configuration;
  let embeddedFontPromise;
  let pdfBytesPromise;
  let dependenciesPromise;
  const hostScript = document.currentScript;
  const settings = {
    pdfUrl: hostScript && hostScript.dataset.mcPdfUrl,
    engineUrl: hostScript && hostScript.dataset.mcPdfEngineUrl,
    dataUrl: hostScript && hostScript.dataset.mcPdfDataUrl
  };

  const fromBase64 = value => {
    const binary = global.atob(value.replace(/\s/g, ''));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
  };

  // Clone values before any await: exports always represent the state at click.
  function snapshotContract(contract) {
    const clone = contract.cloneNode(true);
    const fields = Array.from(contract.querySelectorAll('input, textarea, select'));
    const copies = Array.from(clone.querySelectorAll('input, textarea, select'));
    fields.forEach((field, index) => {
      const copy = copies[index];
      if (field.tagName === 'SELECT') {
        Array.from(field.options).forEach((option, optionIndex) => {
          copy.options[optionIndex].selected = option.selected;
        });
      } else {
        copy.value = field.value;
        if ('checked' in field) copy.checked = field.checked;
      }
      copy.disabled = field.disabled;
    });
    return clone;
  }

  function loadScript(url, ready) {
    if (ready()) return Promise.resolve();
    if (!url) return Promise.reject(new Error('Une ressource PDF est manquante.'));
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      let timer;
      const fail = () => {
        global.clearTimeout(timer);
        script.remove();
        reject(new Error('Le chargement des outils PDF a échoué.'));
      };
      script.src = url;
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        global.clearTimeout(timer);
        if (ready()) resolve();
        else fail();
      };
      script.onerror = fail;
      timer = global.setTimeout(fail, 30000);
      document.head.appendChild(script);
    });
  }

  function loadDependencies() {
    if (!dependenciesPromise) {
      dependenciesPromise = Promise.all([
        loadScript(settings.engineUrl, () => Boolean(global.PDFLib && global.fontkit)),
        loadScript(settings.dataUrl, () => Boolean(global.mcContractPdfConfiguration))
      ]).catch(error => { dependenciesPromise = null; throw error; });
    }
    return dependenciesPromise;
  }

  function loadPdfBytes() {
    if (!pdfBytesPromise) {
      if (!settings.pdfUrl) return Promise.reject(new Error('L’adresse du PDF est manquante.'));
      pdfBytesPromise = (async () => {
        const controller = new AbortController();
        const timer = global.setTimeout(() => controller.abort(), 30000);
        try {
          // Only static files are requested. No answers or personal values leave this page.
          const response = await fetch(settings.pdfUrl, {credentials: 'omit', mode: 'cors', signal: controller.signal});
          if (!response.ok) throw new Error('Le modèle PDF est momentanément inaccessible.');
          const bytes = new Uint8Array(await response.arrayBuffer());
          if (String.fromCharCode(...bytes.slice(0, 5)) !== '%PDF-') throw new Error('Le modèle reçu n’est pas un PDF.');
          return bytes;
        } finally { global.clearTimeout(timer); }
      })().catch(error => { pdfBytesPromise = null; throw error; });
    }
    return pdfBytesPromise;
  }

  function getConfiguration() {
    if (configuration) return configuration;
    const value = global.mcContractPdfConfiguration;
    if (!value || !value.fontBase64 || !Array.isArray(value.fields)) throw new Error('Le modèle PDF est incomplet.');
    configuration = value;
    return configuration;
  }

  async function personalizationSource(bytes, data) {
    // The hosted file has an appended Content Credentials revision. Its original,
    // approved PDF is an exact prefix. PDFLib must fill that original revision.
    // Validate every byte before using the prefix; never truncate an unknown PDF.
    const length = Number(data.originalByteLength);
    if (!Number.isSafeInteger(length) || length < 5 || bytes.length < length ||
        !global.crypto || !global.crypto.subtle) {
      throw new Error('La vérification du modèle PDF est indisponible.');
    }
    const original = bytes.slice(0, length);
    const digest = new Uint8Array(await global.crypto.subtle.digest('SHA-256', original));
    const hash = Array.from(digest, value => value.toString(16).padStart(2, '0')).join('');
    if (hash !== data.originalSha256) throw new Error('Le modèle PDF a changé. Sa configuration doit être actualisée.');
    return original;
  }

  const fieldType = field => {
    if (field.tagName.toLowerCase() === 'textarea') return 'textarea';
    if (field.tagName.toLowerCase() === 'select') return 'select';
    return field.type || 'text';
  };

  function currentControls(contract) {
    const controls = Array.from(contract.querySelectorAll('input, textarea, select'));
    const byName = new Map();
    controls.forEach(field => {
      if (!field.name) return;
      if (!byName.has(field.name)) byName.set(field.name, []);
      byName.get(field.name).push(field);
    });
    return {controls, byName};
  }

  function findControl(record, live) {
    const candidates = live.byName.get(record.htmlName) || [];
    if (record.htmlType === 'checkbox' || record.htmlType === 'radio') {
      return candidates.find(field => fieldType(field) === record.htmlType &&
        String(field.value) === String(record.htmlValue));
    }
    return candidates.find(field => !['checkbox', 'radio'].includes(fieldType(field)));
  }

  function displayedValue(field) {
    const value = String(field.value || '');
    if (fieldType(field) === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-');
      return `${day}/${month}/${year}`;
    }
    return value;
  }

  function exportValue(field, record, live) {
    let value = displayedValue(field);
    // The approved PDF combines dates and optional times in a single field.
    const timeNames = {
      mc_calendrier_debut_date: 'mc_calendrier_debut_heure',
      mc_calendrier_fin_date: 'mc_calendrier_fin_heure'
    };
    const timeName = timeNames[record.htmlName];
    const timeField = timeName && (live.byName.get(timeName) || [])[0];
    const time = timeField && String(timeField.value || '').trim();
    if (time) value = value ? `${value} à ${time}` : time;
    return value;
  }

  function complementLabel(field, record) {
    if (record.section) return `${record.section} — ${record.label || record.htmlName}`;
    const block = typeof field.closest === 'function' && field.closest('.mc-contract-block');
    const heading = block && block.querySelector('h3');
    const number = heading && heading.textContent.match(/^\s*(\d+)\./);
    const label = record.label || record.htmlName;
    return number ? `Section ${number[1]} — ${label}` : label;
  }

  function wrapText(value, font, size, width) {
    const lines = [];
    const paragraphs = String(value).replace(/\r\n?/g, '\n').replace(/\t/g, '    ').split('\n');
    const measure = text => font.widthOfTextAtSize(text, size);
    paragraphs.forEach(paragraph => {
      if (!paragraph) {
        lines.push('');
        return;
      }
      let line = '';
      const tokens = paragraph.match(/\S+\s*|\s+/g) || [paragraph];
      tokens.forEach(token => {
        const candidate = line + token;
        if (measure(candidate.trimEnd()) <= width) {
          line = candidate;
          return;
        }
        if (line.trim()) {
          lines.push(line.trimEnd());
          line = '';
        }
        let remaining = token.trimStart();
        while (remaining && measure(remaining.trimEnd()) > width) {
          const characters = Array.from(remaining);
          let low = 1;
          let high = characters.length;
          while (low < high) {
            const middle = Math.ceil((low + high) / 2);
            if (measure(characters.slice(0, middle).join('')) <= width) low = middle;
            else high = middle - 1;
          }
          const take = Math.max(1, low);
          lines.push(characters.slice(0, take).join(''));
          remaining = characters.slice(take).join('');
        }
        line = remaining;
      });
      lines.push(line.trimEnd());
    });
    return lines;
  }

  async function loadTextFont(pdf, data) {
    if (data.fontBase64 && global.fontkit) {
      pdf.registerFontkit(global.fontkit);
      // Font objects belong to one PDF document; the reusable promise contains only font bytes.
      if (!embeddedFontPromise) embeddedFontPromise = Promise.resolve(fromBase64(data.fontBase64));
      // Keep the full font so the downloaded form can display characters entered later offline.
      return pdf.embedFont(await embeddedFontPromise, {subset: false});
    }
    return pdf.embedFont(global.PDFLib.StandardFonts.Helvetica);
  }

  function registerFormFont(pdf, form, font) {
    const {PDFName, PDFDict} = global.PDFLib;
    let resources = form.acroForm.dict.lookupMaybe(PDFName.of('DR'), PDFDict);
    if (!resources) {
      resources = pdf.context.obj({});
      form.acroForm.dict.set(PDFName.of('DR'), resources);
    }
    let fonts = resources.lookupMaybe(PDFName.of('Font'), PDFDict);
    if (!fonts) {
      fonts = pdf.context.obj({});
      resources.set(PDFName.of('Font'), fonts);
    }
    // PDF editors need this resource when they regenerate an appearance after an offline edit.
    fonts.set(PDFName.of(font.name), font.ref);
  }

  function addComplements(pdf, font, complements) {
    if (!complements.length) return;
    const {rgb} = global.PDFLib;
    const width = 595.28;
    const height = 841.89;
    const margin = 38;
    const textWidth = width - margin * 2;
    const size = 9.2;
    const lineHeight = 12.4;
    let page;
    let y;
    let number = 0;

    function newPage() {
      page = pdf.addPage([width, height]);
      number += 1;
      y = height - margin;
      page.drawText('Compléments des champs remplis au téléchargement', {
        x: margin, y: y - 13, size: 12.2, font, color: rgb(0.45, 0.07, 0.21)
      });
      y -= 31;
      page.drawText('Ces textes reprennent intégralement les champs dépassant leur cadre.', {
        x: margin, y, size: 8.4, font, color: rgb(0.25, 0.25, 0.25)
      });
      y -= 24;
      page.drawText(`Complément ${number}`, {x: margin, y: 23, size: 8, font, color: rgb(0.4, 0.4, 0.4)});
    }

    newPage();
    complements.forEach(complement => {
      const labelLines = wrapText(complement.label, font, 10, textWidth);
      if (y - labelLines.length * 14 - 27 < margin) newPage();
      labelLines.forEach(line => {
        page.drawText(line, {x: margin, y, size: 10, font, color: rgb(0, 0, 0)});
        y -= 14;
      });
      const contentLines = wrapText(complement.value, font, size, textWidth);
      contentLines.forEach(line => {
        if (y - lineHeight < margin) {
          newPage();
          wrapText(`${complement.label} (suite)`, font, 9, textWidth).forEach(headingLine => {
            page.drawText(headingLine, {x: margin, y, size: 9, font, color: rgb(0.3, 0.3, 0.3)});
            y -= 12;
          });
          y -= 6;
        }
        if (line) page.drawText(line, {x: margin, y, size, font, color: rgb(0, 0, 0)});
        y -= lineHeight;
      });
      y -= 15;
    });
  }

  async function buildContractPdf(contract) {
    const live = currentControls(snapshotContract(contract));
    const hasValues = live.controls.some(field =>
      ['checkbox', 'radio'].includes(fieldType(field)) ? field.checked : String(field.value || '') !== '');
    if (!hasValues) return loadPdfBytes();
    const [baseBytes] = await Promise.all([loadPdfBytes(), loadDependencies()]);
    const data = getConfiguration();
    if (!global.PDFLib) throw new Error('Le moteur PDF est indisponible.');

    const pdf = await global.PDFLib.PDFDocument.load(await personalizationSource(baseBytes, data));
    const form = pdf.getForm();
    const textFont = await loadTextFont(pdf, data);
    registerFormFont(pdf, form, textFont);
    const handledRadioGroups = new Set();
    const mappedNames = new Set();
    const complements = [];

    data.fields.forEach(record => {
      const field = findControl(record, live);
      if (!field) return;
      mappedNames.add(record.htmlName);
      if (record.htmlType === 'checkbox') {
        const target = form.getCheckBox(record.pdfName);
        field.checked ? target.check() : target.uncheck();
        return;
      }
      if (record.htmlType === 'radio') {
        if (handledRadioGroups.has(record.pdfName)) return;
        handledRadioGroups.add(record.pdfName);
        const groupRecords = data.fields.filter(item => item.htmlType === 'radio' && item.pdfName === record.pdfName);
        const selected = groupRecords.find(item => {
          const control = findControl(item, live);
          return control && control.checked;
        });
        const target = form.getRadioGroup(record.pdfName);
        selected ? target.select(String(selected.pdfValue == null ? selected.htmlValue : selected.pdfValue)) : target.clear();
        return;
      }

      const value = exportValue(field, record, live);
      const target = form.getTextField(record.pdfName);
      // Merged PDF fields may inherit their appearance; give filled exports an explicit font.
      target.acroField.setDefaultAppearance(`/${textFont.name} ${Number(record.fontSize) || 9.2} Tf 0 g`);
      target.setText(value);
      if (value) {
        const size = Number(record.fontSize) || 9.2;
        target.setFontSize(size);
        const innerWidth = Number(record.innerWidth) || Math.max(10, record.rect[2] - 8);
        const innerHeight = Number(record.innerHeight) || Math.max(10, record.rect[3] - 6);
        const capacity = record.multiline ? Math.max(1, Math.floor(innerHeight / (size * 1.22))) : 1;
        const lines = wrapText(value, textFont, size, innerWidth);
        if (lines.length > capacity || (!record.multiline && /[\r\n]/.test(value))) {
          complements.push({label: complementLabel(field, record), value});
          // A very long value must not trigger expensive layout inside a small fixed-height widget.
          // Its visible appearance contains the start; the canonical PDF value and the complement
          // both retain the complete text. Low-level value restoration does not dirty the appearance.
          const preview = lines.slice(0, capacity).join('\n');
          target.setText(preview);
          target.updateAppearances(textFont);
          target.acroField.setValue(global.PDFLib.PDFHexString.fromText(value));
        } else {
          target.updateAppearances(textFont);
        }
      }
    });

    // The web form can add custom rules beyond the three fields in the blank PDF.
    live.controls.forEach(field => {
      if (mappedNames.has(field.name) || !/^mc_section23_regle_\d+$/.test(field.name)) return;
      const value = displayedValue(field);
      if (value.trim()) {
        const number = field.name.match(/\d+$/)[0];
        complements.push({label: `Section 23 — Règle personnalisée n° ${number}`, value});
      }
    });
    addComplements(pdf, textFont, complements);

    // Checkboxes and radios keep valid appearances; filled text fields were rendered with the embedded font.
    form.updateFieldAppearances(textFont);
    pdf.setTitle('Contrat BDSM — formulaire remplissable');
    pdf.setCreator('Monsieur Chasteté — export local');
    return pdf.save({updateFieldAppearances: false});
  }

  async function downloadContractPdf(contract) {
    const bytes = await buildContractPdf(contract);
    const objectUrl = global.URL.createObjectURL(new Blob([bytes], {type: 'application/pdf'}));
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = 'contrat-bdsm-remplissable.pdf';
    anchor.hidden = true;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Give the browser time to start the local download before releasing the URL.
    global.setTimeout(() => global.URL.revokeObjectURL(objectUrl), 30000);
  }

  global.mcBuildContractPdf = buildContractPdf;
  global.mcDownloadContractPdf = downloadContractPdf;
})(window);



(() => {
  'use strict';
  const contract = document.getElementById('mc-bdsm-contract');
  if (!contract) return;
  const fieldSelector = 'input, textarea, select';
  const choiceSelector = 'input[type="checkbox"], input[type="radio"]';
  const fields = Array.from(contract.querySelectorAll(fieldSelector));
  const rows = Array.from(contract.querySelectorAll('.mc-choice-row'));
  const modules = Array.from(contract.querySelectorAll('.mc-module'));
  const baseDisabled = new WeakMap();
  const describedBeforeNote = new Map();
  const describedBeforeScope = new Map();
  let pendingPrintMode = null;
  let noteSequence = 0;

  const choiceFor = row => {
    const label = Array.from(row.children).find(child => child.matches('label.mc-choice'));
    return label ? label.querySelector(choiceSelector) : null;
  };
  // Every subject stays visible. Only a detail field linked to an unchecked
  // answer is disabled; optional sections never require an activation checkbox.
  fields.forEach(field => {
    const dynamicallyControlled = field.closest('.mc-linked-fields');
    baseDisabled.set(field, field.disabled && !dynamicallyControlled);
    if (field.matches(choiceSelector)) {
      field.checked = false;
      field.defaultChecked = false;
    }
  });
  const groupForName = name => {
    const field = fields.find(field => field.name === name);
    return field && field.closest('.mc-choice-group');
  };
  const scopeRules = [
    { controller: 'mc_section15_place', blockedValues: ['none'], targetSelector: '.mc-section15-details',
      text: 'Aucun scénario ni fétiche particulier n’est inclus : les parties B à D ne sont pas à remplir.' },
    { controller: 'mc_section20_captures', blockedValues: ['none'], targetSelector: '.mc-section20-capture-decision',
      text: 'Aucune capture n’est autorisée : le mode de décision des captures n’est pas à remplir.' },
    { controller: 'mc_section20_captures', blockedValues: ['none'], targetSelector: '.mc-section20-content-details',
      text: 'La partie C n’est pas à remplir. Les parties D à F restent disponibles pour les contenus existants.' },
    { controller: 'mc_section20_conservation', blockedValues: ['none'], targetSelector: '.mc-section20-storage-details',
      text: 'Aucune conservation ultérieure n’est prévue : les modalités de conservation ne sont pas à remplir.' },
    { controller: 'mc_section20_diffusion', blockedValues: ['none'], targetSelector: '.mc-section20-sharing-details',
      text: 'Aucun envoi, partage ni publication n’est prévu : le champ de conditions de diffusion n’est pas à remplir.' },
    { controller: 'mc_section19_depenses', blockedValues: ['none'], targetSelector: '.mc-section19-expense-details',
      text: 'Aucune dépense n’est prévue : le champ de prise en charge et de remboursement n’est pas à remplir.' },
    { controller: 'mc_section19_arrangements', blockedValues: ['none'], targetSelector: '.mc-section19-gift-details',
      text: 'Aucun arrangement n’est prévu : le champ de conditions des cadeaux ou versements n’est pas à remplir.' },
    { controller: 'mc_section19_arrangements', allowedValues: ['pret'], targetSelector: '.mc-section19-loan-details',
      text: 'Le champ de restitution est à remplir uniquement si un prêt de biens ou de matériel est retenu.' },
    { controller: 'mc_section19_findom', blockedValues: ['none'], targetSelector: '.mc-section19-findom-details',
      text: 'Aucun jeu de domination financière n’est prévu : le reste de la partie C n’est pas à remplir. Les autres parties restent disponibles.' },
    { controller: 'mc_section18_place', blockedValues: ['none'], targetSelector: '.mc-section18-control-details',
      text: 'Aucun contrôle à distance n’est prévu : les parties C et D ne sont pas à remplir. Les communications et les partages volontaires des parties B, E et F restent disponibles.' },
    { controller: 'mc_section18_commande', blockedValues: ['none'], targetSelector: '.mc-section18-device-details',
      text: 'Aucune commande d’accessoire à distance n’est prévue : le champ de conditions de commande n’est pas à remplir.' },
    { controller: 'mc_section18_localisation', blockedValues: ['none'], targetSelector: '.mc-section18-location-details',
      text: 'Aucun partage de localisation n’est prévu : le champ de conditions de partage n’est pas à remplir.' },
    { controller: 'mc_section18_acces', blockedValues: ['none'], targetSelector: '.mc-section18-access-details',
      text: 'Aucun accès n’est prévu : le champ de personnes autorisées, d’accès et de limites n’est pas à remplir.' },
    { controller: 'mc_section17_place', blockedValues: ['none'], targetSelector: '.mc-section17-details',
      text: 'Aucun tiers n’est prévu pendant les séances : les parties B et C ne sont pas à remplir. Les autres partenaires en dehors des séances peuvent être précisés dans la partie D.' },
    { controller: 'mc_section16_place', blockedValues: ['none'], targetSelector: '.mc-section16-details',
      text: 'Aucun jeu d’humiliation, de dégradation symbolique ou d’exposition n’est prévu : les parties B à D ne sont pas à remplir.' },
    { controller: 'mc_section14_nature', blockedValues: ['none'], targetSelector: '.mc-section14-consequence-details',
      text: 'Aucune conséquence n’est prévue : les parties B, C et D ne sont pas à remplir. La partie E concernant les récompenses reste disponible.' },
    { controller: 'mc_section14_recompenses', blockedValues: ['none'], fieldNames: ['mc_section14_recompenses_details'],
      text: 'Aucune récompense particulière n’est retenue : le champ de conditions d’attribution n’est pas à remplir.' },
    { controller: 'mc_section13_niveau', blockedValues: ['none'], targetSelector: '.mc-section13-details',
      text: 'Aucun protocole particulier n’est retenu : les parties B, C et E ne sont pas à remplir. Les parties D et F restent disponibles.' },
    { controller: 'mc_section13_symbole_port', blockedValues: ['none'], targetSelector: '.mc-section13-symbol-details',
      text: 'Aucun collier, bijou ou symbole particulier n’est retenu : les champs de description et de précisions complémentaires ne sont pas à remplir.' },
    { controller: 'mc_section12_place_sexualite', blockedValues: ['none'], targetSelector: '.mc-section12-shared-sexuality',
      text: 'Les parties B à F ne sont pas à remplir lorsque les activités sexuelles ne sont pas incluses dans le présent contrat. Les parties G à I restent disponibles.' },
    { controller: 'mc_section12_chastete_rythme', blockedValues: ['none'], targetSelector: '.mc-section12-chastity-details',
      text: 'Aucune règle de chasteté n’est prévue : la suite de cette partie n’est pas à remplir.' },
    { controller: 'mc_section12_chastete_dispositif', blockedValues: ['sans'], targetSelector: '.mc-section12-device-details',
      text: 'Les champs concernant le dispositif ne sont pas à remplir lorsque la chasteté est prévue sans dispositif physique.' },
    { controller: 'mc_section12_fluides', blockedValues: ['non'], fieldNames: ['mc_section12_fluides_details'],
      text: 'Les échanges de fluides ne sont pas acceptés : le champ de précisions associé est inactif.' },
    { controller: 'mc_section12_depistage', blockedValues: ['non', 'discussion'], fieldNames: ['mc_section12_depistage_details'],
      text: 'Le champ de modalités est à remplir lorsque des dispositions concernant le dépistage des IST sont convenues.' }
  ].map((rule, index) => {
    const group = groupForName(rule.controller);
    const controllers = fields.filter(field => field.name === rule.controller);
    const targetGroups = (rule.groupNames || []).map(groupForName).filter(Boolean);
    const module = rule.moduleId && document.getElementById(rule.moduleId);
    const scopedTargets = rule.targetSelector ? Array.from(contract.querySelectorAll(rule.targetSelector)) : [];
    const targets = group ? fields.filter(field =>
      scopedTargets.length ? scopedTargets.some(target => target.contains(field))
        : module ? module.contains(field) && !group.contains(field)
        : targetGroups.some(target => target.contains(field)) || (rule.fieldNames || []).includes(field.name)) : [];
    return { ...rule, group, controllers, targets, noteId: `mc-scope-note-${index + 1}`, note: null };
  }).filter(rule => rule.group && rule.targets.length);
  const scopeBlockedFields = () => {
    const blocked = new Set();
    describedBeforeScope.forEach((previous, field) => {
      if (previous === null) field.removeAttribute('aria-describedby');
      else field.setAttribute('aria-describedby', previous);
    });
    describedBeforeScope.clear();
    scopeRules.forEach(rule => {
      const active = rule.allowedValues
        ? !rule.controllers.some(field => field.checked && rule.allowedValues.includes(field.value))
        : rule.controllers.some(field => field.checked && rule.blockedValues.includes(field.value));
      if (!active) {
        if (rule.note) rule.note.remove();
        rule.note = null;
        return;
      }
      if (!rule.note) {
        const note = document.createElement('p');
        note.id = rule.noteId;
        note.className = 'mc-scope-status';
        note.setAttribute('role', 'status');
        note.setAttribute('aria-live', 'polite');
        note.textContent = rule.text;
        const legend = Array.from(rule.group.children).find(child => child.tagName === 'LEGEND');
        if (legend) legend.after(note);
        else rule.group.prepend(note);
        rule.note = note;
      }
      rule.targets.forEach(field => {
        blocked.add(field);
        if (field.matches(choiceSelector)) field.checked = false;
        if (!describedBeforeScope.has(field)) describedBeforeScope.set(field, field.getAttribute('aria-describedby'));
        field.setAttribute('aria-describedby', [field.getAttribute('aria-describedby'), rule.noteId].filter(Boolean).join(' '));
      });
    });
    return blocked;
  };
  const syncState = () => {
    contract.querySelectorAll('.mc-module-content').forEach(content => { content.hidden = false; });
    const scopeBlocked = scopeBlockedFields();
    fields.forEach(field => {
      const linked = field.closest('.mc-linked-fields');
      const row = linked && linked.closest('.mc-choice-row');
      const choice = row && choiceFor(row);
      field.disabled = Boolean(baseDisabled.get(field)) || scopeBlocked.has(field)
        || Boolean(linked && (!choice || !choice.checked));
    });
    rows.forEach(row => {
      const choice = choiceFor(row);
      row.classList.toggle('mc-selected', Boolean(choice && choice.checked));
      row.classList.toggle('mc-inactive', Boolean(choice && choice.disabled));
    });
  };

  const clearNotes = () => {
    contract.querySelectorAll('.mc-validation-status').forEach(note => note.remove());
    describedBeforeNote.forEach((previous, field) => {
      if (previous === null) field.removeAttribute('aria-describedby');
      else field.setAttribute('aria-describedby', previous);
    });
    describedBeforeNote.clear();
  };
  const fieldLabel = field => {
    const associated = field.labels && field.labels[0];
    return associated ? associated.textContent.trim() : (field.getAttribute('aria-label') || 'le champ associé');
  };
  const requiredUnfilled = field => field.matches('[data-required-choice]')
    && !field.disabled && String(field.value || '').trim() === '';
  const annotateChoices = (focusFirst = false) => {
    clearNotes();
    let first = null;
    rows.forEach(row => {
      const choice = choiceFor(row);
      if (!choice || !choice.checked || choice.disabled) return;
      const missing = Array.from(row.querySelectorAll('.mc-linked-fields [data-required-choice]'))
        .filter(field => field.matches(fieldSelector) && requiredUnfilled(field));
      if (!missing.length) return;
      const note = document.createElement('p');
      note.className = 'mc-validation-status';
      note.id = `mc-choice-note-${++noteSequence}`;
      note.setAttribute('role', 'status');
      note.setAttribute('aria-live', 'polite');
      note.textContent = `À préciser : ${missing.map(fieldLabel).join(' ; ')}. Vous pouvez compléter ces champs à la main après impression.`;
      row.append(note);
      missing.forEach(field => {
        const previous = field.getAttribute('aria-describedby');
        describedBeforeNote.set(field, previous);
        field.setAttribute('aria-describedby', [previous, note.id].filter(Boolean).join(' '));
      });
      if (!first) first = missing[0];
    });
    if (focusFirst && first) first.focus();
  };

  const enforceExclusive = changed => {
    if (!changed.matches('input[type="checkbox"]') || !changed.checked) return;
    const group = changed.closest('fieldset[data-exclusive]');
    if (!group) return;
    const siblings = Array.from(group.querySelectorAll('input[type="checkbox"]'))
      .filter(field => field.closest('fieldset[data-exclusive]') === group);
    const isNone = field => field.value === 'none' || field.hasAttribute('data-exclusive-none');
    const isCustom = field => field.hasAttribute('data-exclusive-custom');
    // A personal appellation is an alternative to every predefined title in
    // this group. It is exclusive as soon as its checkbox is selected, even
    // before typing. Normal predefined titles may still be combined.
    if (isNone(changed) || isCustom(changed)) {
      siblings.forEach(field => { if (field !== changed) field.checked = false; });
    } else {
      siblings.filter(field => isNone(field) || isCustom(field))
        .forEach(field => { field.checked = false; });
    }
  };
  contract.addEventListener('change', event => {
    if (!event.target.matches(fieldSelector)) return;
    enforceExclusive(event.target);
    clearNotes();
    syncState();
  });
  contract.addEventListener('input', event => {
    if (event.target.matches(fieldSelector)) clearNotes();
  });
  // Register added rules in DOM order so printing cannot shift later fields.
  const personalRules = contract.querySelector('#mc-personal-rules');
  const addRuleButton = contract.querySelector('#mc-add-personal-rule');
  const addPersonalRule = () => {
    if (!personalRules) return;
    const next = personalRules.querySelectorAll('textarea').length + 1;
    const label = document.createElement('label');
    label.className = 'mc-field-label';
    label.htmlFor = `mc-personal-rule-${next}`;
    label.append(document.createTextNode(`Règle personnalisée n° ${next} :`));
    const field = document.createElement('textarea');
    field.id = `mc-personal-rule-${next}`;
    field.name = `mc_section23_regle_${next}`;
    field.rows = 1;
    field.placeholder = 'Ex. : personne concernée ; règle ; moments ; exceptions.';
    label.append(field);
    personalRules.append(label);
    baseDisabled.set(field, false);
    fields.splice(0, fields.length, ...contract.querySelectorAll(fieldSelector));
    clearNotes();
    syncState();
    field.focus();
  };
  if (personalRules && addRuleButton) {
    addRuleButton.type = 'button';
    addRuleButton.hidden = false;
    addRuleButton.addEventListener('click', addPersonalRule);
  }

  // No answers are submitted or persisted. PDF export fetches only static technical files.
  contract.querySelectorAll('form').forEach(form => {
    form.addEventListener('submit', event => event.preventDefault());
  });

  const removePrintDocument = () => {
    const existing = document.getElementById('mc-print-document');
    if (existing) existing.remove();
  };
  const preparePrintDocument = (mode = 'complete') => {
    removePrintDocument();
    const blank = mode === 'blank';
    const clone = contract.cloneNode(true);
    const clonedFields = Array.from(clone.querySelectorAll(fieldSelector));
    clone.querySelectorAll('[hidden]').forEach(node => { node.hidden = false; });
    clonedFields.forEach((copiedField, index) => {
      const field = fields[index];
      const type = String(field.type || '').toLowerCase();
      if (['hidden', 'button', 'submit', 'reset', 'image'].includes(type)) {
        copiedField.remove();
        return;
      }
      const value = document.createElement('span');
      value.className = 'mc-print-value';
      if (type === 'checkbox' || type === 'radio') {
        value.classList.add('mc-print-choice', 'mc-print-value-inline');
        value.textContent = !blank && field.checked ? '☑ ' : '☐ ';
      } else {
        value.classList.add('mc-print-value-block');
        const current = blank ? '' : field.tagName === 'SELECT'
          ? Array.from(field.selectedOptions).map(option => option.textContent).join(', ')
          : String(field.value || '');
        value.textContent = current.trim() !== '' ? current : '________________';
        if (type === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(current)) {
          const [year, month, day] = current.split('-');
          value.textContent = `${day}/${month}/${year}`;
        }
        if (field.classList.contains('mc-signature-field')) {
          value.classList.add('mc-signature-frame');
          if (current.trim() === '') value.textContent = '';
        }
      }
      copiedField.replaceWith(value);
    });
    clone.querySelectorAll('button, script, style, .mc-contract-actions, .mc-nojs, .mc-validation-status, .mc-scope-status, [data-mc-print-hide]')
      .forEach(node => node.remove());
    clone.querySelectorAll('[data-mc-contract-title]').forEach(heading => heading.remove());
    clone.removeAttribute('id');
    clone.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
    clone.querySelectorAll('label[for]').forEach(label => label.removeAttribute('for'));
    clone.querySelectorAll('[aria-controls], [aria-expanded], [aria-describedby]').forEach(node => {
      node.removeAttribute('aria-controls');
      node.removeAttribute('aria-expanded');
      node.removeAttribute('aria-describedby');
    });
    const documentRoot = document.createElement('div');
    documentRoot.id = 'mc-print-document';
    documentRoot.className = 'mc-page';
    const title = document.createElement('h1');
    title.textContent = 'Contrat BDSM Monsieur Chasteté';
    const modeLabel = document.createElement('p');
    modeLabel.className = 'mc-print-mode';
    modeLabel.textContent = blank
      ? 'Contrat vierge — à compléter'
      : 'Version personnalisée — réponses saisies sur cette page';
    documentRoot.append(title, modeLabel, clone);
    document.body.append(documentRoot);
  };

  const currentPrintMode = () => fields.some(field => {
    const type = String(field.type || '').toLowerCase();
    if (['hidden', 'button', 'submit', 'reset', 'image'].includes(type)) return false;
    return type === 'checkbox' || type === 'radio'
      ? field.checked : String(field.value || '') !== '';
  }) ? 'complete' : 'blank';

  const printContract = () => {
    const mode = currentPrintMode();
    pendingPrintMode = mode;
    preparePrintDocument(mode);
    // Keep the clone until afterprint: browsers may prepare their preview
    // asynchronously after print() returns. A timer could remove it too early.
    window.print();
  };
  document.querySelectorAll('[data-mc-action]').forEach(button => {
    button.type = 'button';
    button.hidden = false;
    if (button.dataset.mcAction === 'print') button.addEventListener('click', printContract);
    if (button.dataset.mcAction === 'download-pdf') button.addEventListener('click', async () => {
      const status = document.getElementById(button.dataset.mcStatusTarget || 'mc-export-status');
      const downloadButtons = document.querySelectorAll('[data-mc-action="download-pdf"]');
      document.querySelectorAll('[data-mc-export-status]').forEach(node => { node.textContent = ''; });
      downloadButtons.forEach(node => { node.disabled = true; });
      if (status) status.textContent = 'Préparation du PDF…';
      try {
        await window.mcDownloadContractPdf(contract);
        if (status) status.textContent = 'PDF téléchargé. Vos réponses restent affichées sur cette page et figurent dans le PDF téléchargé. Monsieur Chasteté ne reçoit ni ne conserve vos réponses.';
      } catch (error) {
        if (status) status.textContent = 'Le téléchargement a échoué. Réessayez ou utilisez « Imprimer le contrat BDSM ». ';
        console.error('Échec de la création du PDF', error);
      } finally { downloadButtons.forEach(node => { node.disabled = false; }); }
    });
    if (button.dataset.mcAction === 'reset') {
      button.addEventListener('click', () => {
        if (!window.confirm('Effacer tous les champs et toutes les sélections du contrat ?')) return;
        fields.forEach(field => {
          const type = String(field.type || '').toLowerCase();
          if (type === 'checkbox' || type === 'radio') field.checked = false;
          else if (field.tagName === 'SELECT') field.selectedIndex = -1;
          else if (!['hidden', 'button', 'submit', 'reset', 'image'].includes(type)) field.value = '';
        });
        clearNotes();
        removePrintDocument();
        pendingPrintMode = null;
        document.querySelectorAll('[data-mc-export-status]').forEach(node => { node.textContent = ''; });
        syncState();
      });
    }
  });
  window.addEventListener('beforeprint', () => {
    const mode = pendingPrintMode || currentPrintMode();
    preparePrintDocument(mode);
  });
  window.addEventListener('afterprint', () => {
    removePrintDocument();
    pendingPrintMode = null;
  });
  syncState();
  document.documentElement.classList.add('mc-js');
})();
